// src/services/studyReportApprovalService.js
//
// Reads and writes for approving coordinators' report numbers on a study
// (rules: studyReportApproval.js). An approval is one transaction that
// re-reads the study and the reports, refuses if any report was decided in the
// meantime, adds to ENROLLED / SCREEN_FAILURES only, and stamps each report.

import {
  FieldPath,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { getStudiesPath, getWeeklyReportPatientsPath, getWeeklyReportsPath } from "./firestorePaths";
import { buildStudyStamp, pendingStudyEntries, planStudyUpdate, studiesWithPendingNumbers } from "./studyReportApproval";

/**
 * Pending report entries for a study, each with that study's patients from
 * the report (admins can read the patient lists; a failed read just leaves
 * the list empty).
 */
export const loadPendingForStudy = async (studyDocId) => {
  const snap = await getDocs(
    query(collection(db, getWeeklyReportsPath()), where("studyDocIds", "array-contains", studyDocId))
  );
  const entries = pendingStudyEntries(
    snap.docs.map((d) => ({ id: d.id, ...d.data() })),
    studyDocId
  );
  const withPatients = await Promise.all(
    entries.map(async (e) => {
      try {
        const p = await getDoc(doc(db, getWeeklyReportPatientsPath(), e.reportId));
        const study = (p.exists() ? p.data().studies || [] : []).find((s) => s.studyDocId === studyDocId);
        return { ...e, patients: study?.patients || [] };
      } catch (err) {
        console.error("❌ Could not load a report's patients:", err);
        return { ...e, patients: [] };
      }
    })
  );
  return withPatients;
};

/** Ids of this site's studies with report numbers waiting for an admin (Dashboard highlight). */
export const loadStudiesNeedingApproval = async () => {
  const snap = await getDocs(query(collection(db, getWeeklyReportsPath()), where("format", "==", 2)));
  return studiesWithPendingNumbers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
};

/**
 * Approve (add to the study's numbers) or dismiss these reports for a study.
 * Returns the study's new { ENROLLED, SCREEN_FAILURES } on approval, else null.
 */
export const decideStudyReports = async ({ studyDocId, reportIds, status, by }) => {
  if (!reportIds?.length) return null;
  return runTransaction(db, async (tx) => {
    const studyRef = doc(db, getStudiesPath(), studyDocId);
    const reportRefs = reportIds.map((id) => doc(db, getWeeklyReportsPath(), id));
    const studySnap = await tx.get(studyRef);
    if (!studySnap.exists()) throw new Error("This study no longer exists.");
    const reportSnaps = [];
    for (const ref of reportRefs) reportSnaps.push(await tx.get(ref));

    const entries = pendingStudyEntries(
      reportSnaps.filter((s) => s.exists()).map((s) => ({ id: s.id, ...s.data() })),
      studyDocId
    );
    if (entries.length !== reportIds.length) {
      throw new Error("Someone already decided on one of these reports. Refresh the page to see the latest.");
    }

    let after = null;
    let steps = [];
    if (status === "approved") {
      const plan = planStudyUpdate(studySnap.data(), entries);
      after = plan.after;
      steps = plan.steps;
      tx.update(studyRef, { ENROLLED: after.ENROLLED, SCREEN_FAILURES: after.SCREEN_FAILURES });
    }
    entries.forEach((entry, i) => {
      const stamp = buildStudyStamp({ status, by, entry, step: steps[i] });
      tx.update(reportRefs[reportIds.indexOf(entry.reportId)], new FieldPath("studyApprovals", studyDocId), {
        ...stamp,
        at: serverTimestamp(),
      });
    });
    return after;
  });
};

/** studyApprovals for a set of reports (for "My past reports" / Submitted reports). */
export const loadReportApprovals = async (reportIds = []) => {
  const out = {};
  await Promise.all(
    reportIds.map(async (id) => {
      try {
        const s = await getDoc(doc(db, getWeeklyReportsPath(), id));
        if (s.exists()) out[id] = { studies: s.data().studies || [], studyApprovals: s.data().studyApprovals || {} };
      } catch {
        /* the list still shows without the status */
      }
    })
  );
  return out;
};
